import { useState, useEffect, useRef, useCallback } from "react";
import { TrenitaliaLogo } from "../../../shared/components/TrenitaliaLogo";
import { ConfigModal } from "./ConfigModal";
import { Message } from "./Message";
import { ChatInput } from "./ChatInput";
import { FeedbackArea } from "./FeedbackArea";
import { QuickActions } from "./QuickActions";
import { API_BASE_URL, API_ENDPOINTS } from "../../../config/apiConfig";
import { useSnackbar } from "../../snackbar/context/SnackbarContext";
import { useNavigate } from "react-router-dom";
import axiosInstance, { ensureFreshToken } from "../../../config/axiosConfig";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import "./TrenitaliaChat.css";

export const TrenitaliaChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "assistant",
      content:
        "Ciao, sono AI Station Support, l'assistente virtuale di Trenitalia, pronto ad aiutarti.",
      createdAt: Date.now(),
    },
  ]);

  const [isTyping, setIsTyping] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionText, setSuggestionText] = useState("");
  const [messageCount, setMessageCount] = useState(0);
  const [feedbackSent, setFeedbackSent] = useState(new Set());
  const [currentFeedback, setCurrentFeedback] = useState({
    messageId: null,
    isLike: null,
  });
  const [chatWidth, setChatWidth] = useState(650);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("trenitalia_session_id") || null;
  });
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem("trenitalia_chat_config");
    return saved
      ? JSON.parse(saved)
      : {
          event_top_min: 3,
          event_top_max: 20,
          event_score_min: 0,
          event_score_max: 3,
          event_highlights: false,
          msg_top_min: 5,
          msg_top_max: 30,
          msg_score_min: 0,
          msg_score_max: 2,
          msg_highlights: false,
        };
  });

  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const quickActions = [
    "Cosa è successo oggi?",
    "Dammi informazioni sul treno FR 9587",
  ];
  const [currentTraceId, setCurrentTraceId] = useState(null);

  const navigate = useNavigate();

  const { showSnackbarError } = useSnackbar();

  // Funzione per calcolare la larghezza necessaria per le tabelle
  const handleTableResize = useCallback((newWidth) => {
    setChatWidth(newWidth);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      console.log("Session ID attiva:", sessionId);
    } else {
      console.log("Nessuna Session ID - prima richiesta");
    }
  }, [sessionId]);

  const isConfigModified = useCallback(() => {
    const defaultConfig = {
      event_top_min: 3,
      event_top_max: 20,
      event_score_min: 0,
      event_score_max: 3,
      event_highlights: false,
      msg_top_min: 5,
      msg_top_max: 30,
      msg_score_min: 0,
      msg_score_max: 2,
      msg_highlights: false,
    };
    return JSON.stringify(config) !== JSON.stringify(defaultConfig);
  }, [config]);

  const saveSessionId = useCallback((newSessionId) => {
    setSessionId(newSessionId);
    localStorage.setItem("trenitalia_session_id", newSessionId);
  }, []);

  const clearSessionId = useCallback(() => {
    setSessionId(null);
    localStorage.removeItem("trenitalia_session_id");
  }, []);

  const saveConfig = useCallback((newConfig) => {
    setConfig(newConfig);
    localStorage.setItem("trenitalia_chat_config", JSON.stringify(newConfig));
    console.log("Configurazione salvata:", newConfig);
  }, []);

const callAPI = async (message) => {
  const payload = {
    query: message,
    event_top_min: config.event_top_min,
    event_top_max: config.event_top_max,
    event_score_min: config.event_score_min,
    event_score_max: config.event_score_max,
    event_highlights: config.event_highlights,
    msg_top_min: config.msg_top_min,
    msg_top_max: config.msg_top_max,
    msg_score_min: config.msg_score_min,
    msg_score_max: config.msg_score_max,
    msg_highlights: config.msg_highlights,
  };

  if (sessionId) {
    payload.session_id = sessionId;
  }

  console.log("Invio payload con configurazione:", payload);

  const response = await axiosInstance.post(
    `${API_BASE_URL}${API_ENDPOINTS.QUERY}`,
    payload
  );

  const data = response.data;

  if (data.session_id && data.session_id !== sessionId) {
    saveSessionId(data.session_id);
  }

  if (data.trace_id) {
    setCurrentTraceId(data.trace_id);
    console.log("Trace ID salvato:", data.trace_id);
  }

  return {
    risposta:
      data.risposta_sintetizzata ||
      data.response ||
      data.message ||
      "Nessuna risposta ricevuta dal server",
    fonti: data.documenti_sorgente || [],
  };
};

  const sendScoreToBackend = async (traceId, score, comment) => {
    try {
      console.log("Invio score:", { traceId, score, comment });

      const response = await axiosInstance.post(
        `${API_BASE_URL}${API_ENDPOINTS.SCORE}`,
        {
          trace_id: traceId,
          score: score,
          comment: comment || "",
        }
      );

      console.log("Score inviato:", response.data);
      return true;
    } catch (err) {
      showSnackbarError(err.message);
    }
  };

  const streamResponse = async (text, messageId) => {
    const chunkSize = 10; // Chunk size bilanciato per realismo
    let currentIndex = 0;

    while (currentIndex < text.length) {
      // Calcola il prossimo chunk, rispettando i confini delle parole
      let nextIndex = Math.min(currentIndex + chunkSize, text.length);

      // Per le tabelle, evita di fermarsi nel mezzo di una riga
      if (nextIndex < text.length) {
        const nextChar = text[nextIndex];
        const isInTable =
          text.slice(0, nextIndex).includes("|") &&
          text.slice(nextIndex).includes("|");

        if (isInTable && nextChar !== "\n") {
          // Se siamo in una tabella, vai fino alla fine della riga
          while (nextIndex < text.length && text[nextIndex] !== "\n") {
            nextIndex++;
          }
        } else {
          // Comportamento normale per il testo
          while (nextIndex > currentIndex && text[nextIndex] !== " ") {
            nextIndex--;
          }
          if (nextIndex === currentIndex) {
            nextIndex = currentIndex + chunkSize;
          }
        }
      }

      const chunk = text.slice(0, nextIndex);

      // Determina se siamo in una tabella per il delay
      const isCurrentlyInTable =
        chunk.includes("|") && text.slice(nextIndex).includes("|");

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, content: chunk, isTyping: false }
            : msg
        )
      );

      currentIndex = nextIndex;

      // Delay bilanciato per effetto realistico
      const delay =
        text[nextIndex - 1] === "." ||
        text[nextIndex - 1] === "!" ||
        text[nextIndex - 1] === "?"
          ? 2 // Pausa più lunga dopo frasi complete per respiro
          : text[nextIndex - 1] === ","
          ? 2 // Pausa media dopo virgole
          : isCurrentlyInTable
          ? 2 // Più veloce per le tabelle ma comunque visibile
          : 2; // Velocità normale per testo regolare

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  };

  // Parser minimale per Server-Sent Events su uno stream fetch (Fase 2, piano
  // §3.4): NON si usa EventSource (GET-only, niente header custom) — qui
  // serve POST + Authorization Bearer, quindi fetch + ReadableStream a mano.
  // Ogni evento SSE è un blocco "event: <type>\ndata: <json>" separato da una
  // riga vuota; l'ultimo blocco del buffer può essere incompleto (a cavallo
  // tra due chunk di rete) e va riportato al prossimo giro.
  const parseSseChunk = (buffer) => {
    const events = [];
    const parts = buffer.split("\n\n");
    const remainder = parts.pop() || "";

    for (const part of parts) {
      if (!part.trim()) continue;
      let eventType = "message";
      const dataLines = [];
      for (const line of part.split("\n")) {
        if (line.startsWith("event:")) {
          eventType = line.slice("event:".length).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice("data:".length).trim());
        }
      }
      let payload = null;
      try {
        payload = JSON.parse(dataLines.join("\n"));
      } catch (e) {
        continue; // evento malformato: ignorato, mai un crash del consumer
      }
      events.push({ type: eventType, payload });
    }

    return { events, remainder };
  };

  // Consumer reale dello stream /query/stream (Fase 2, piano §3.4): sostituisce
  // il finto streamResponse (rimasto sopra, ora usato SOLO come fallback — vedi
  // handleSendMessage) con token progressivi veri. `error.startedStreaming`
  // (attaccato qui) distingue "stream mai partito" (fallback a /query) da
  // "errore a metà stream" (nessun fallback, stato graceful).
  const streamQuery = async (message, messageId) => {
    const accessToken = await ensureFreshToken();

    const payload = {
      query: message,
      event_top_min: config.event_top_min,
      event_top_max: config.event_top_max,
      event_score_min: config.event_score_min,
      event_score_max: config.event_score_max,
      event_highlights: config.event_highlights,
      msg_top_min: config.msg_top_min,
      msg_top_max: config.msg_top_max,
      msg_score_min: config.msg_score_min,
      msg_score_max: config.msg_score_max,
      msg_highlights: config.msg_highlights,
    };
    if (sessionId) {
      payload.session_id = sessionId;
    }

    let receivedFirstEvent = false;

    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.QUERY_STREAM}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Stream non disponibile (status ${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulatedText = "";
      let fonti = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remainder } = parseSseChunk(buffer);
        buffer = remainder;

        for (const evt of events) {
          receivedFirstEvent = true;

          if (evt.type === "token") {
            accumulatedText += evt.payload?.content || "";
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === messageId ? { ...msg, content: accumulatedText, isTyping: false } : msg
              )
            );
          } else if (evt.type === "sources") {
            fonti = evt.payload?.documenti_sorgente || [];
          } else if (evt.type === "done") {
            if (evt.payload?.session_id && evt.payload.session_id !== sessionId) {
              saveSessionId(evt.payload.session_id);
            }
            if (evt.payload?.trace_id) {
              setCurrentTraceId(evt.payload.trace_id);
              console.log("Trace ID salvato:", evt.payload.trace_id);
            }
          } else if (evt.type === "error") {
            throw new Error(evt.payload?.message || "Errore durante lo streaming");
          }
          // Altri "type" (es. "node", "status" futuri): ignorati — forward
          // compatible col protocollo (piano §10.3), nessun crash del consumer.
        }
      }

      if (!receivedFirstEvent) {
        throw new Error("Nessun evento ricevuto dallo stream");
      }

      return { risposta: accumulatedText, fonti };
    } catch (error) {
      error.startedStreaming = receivedFirstEvent;
      throw error;
    }
  };

  const handleSendMessage = async (text) => {
    if (isTyping) return;

    setIsTyping(true);
    setMessageCount((prev) => prev + 1);

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: text,
      createdAt: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);

    const typingMessage = {
      id: Date.now() + 1,
      type: "assistant",
      content: "",
      createdAt: Date.now(),
      isTyping: true,
    };
    setMessages((prev) => [...prev, typingMessage]);

    try {
      let fonti = [];

      try {
        // Path primario: streaming reale via SSE (Fase 2, piano §3.4).
        // L'indicatore "sto pensando" (isTyping) resta VISIBILE per tutta la
        // fase routing+agenti (decine di secondi senza token): è il PRIMO
        // token della synthesis a spegnerlo (streamQuery imposta
        // isTyping:false insieme al primo contenuto; il fallback fa lo stesso
        // in streamResponse).
        const result = await streamQuery(text, typingMessage.id);
        fonti = result.fonti;
      } catch (streamError) {
        if (streamError.startedStreaming) {
          // Errore A META' stream: NIENTE fallback (mostrerebbe una risposta
          // duplicata/incoerente sopra al testo parziale già visibile) — stato
          // graceful, coerente col vincolo "mai un crash/spinner infinito"
          // (piano §6, ladder di fallback).
          const errorMessage = getApiErrorMessage(streamError);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === typingMessage.id
                ? { ...msg, content: errorMessage, isTyping: false }
                : msg
            )
          );
          setIsTyping(false);
          return;
        }

        // Stream fallito PRIMA del primo evento (piano §3.4: fallback
        // esplicito) — ripiega sul path /query classico non-stream, con la
        // stessa simulazione di digitazione usata prima di questa milestone.
        console.warn(
          "Streaming non disponibile, fallback a /query classico:",
          streamError?.message
        );
        const response = await callAPI(text);
        await streamResponse(response.risposta, typingMessage.id);
        fonti = response.fonti;
      }

      // Aggiungi le fonti dopo aver completato lo streaming
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === typingMessage.id ? { ...msg, fonti: fonti || [] } : msg
        )
      );
    } catch (error) {
      console.error("Error:", error);

      const errorMessage = getApiErrorMessage(error);

      showSnackbarError(error);

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === typingMessage.id
            ? { ...msg, content: errorMessage, isTyping: false }
            : msg
        )
      );
    } finally {
      setIsTyping(false);
    }

    setIsTyping(false);
  };

  const handleRefresh = () => {
    // Catturo il session_id corrente PRIMA di azzerarlo: serve per cancellare il
    // thread lato server (checkpointer) e non lasciarlo orfano.
    const previousSessionId = sessionId;

    setMessages([
      {
        id: 1,
        type: "assistant",
        content:
          "Ciao, sono AI Station Support, l'assistente virtuale di Trenitalia, pronto ad aiutarti.",
        createdAt: Date.now(),
      },
    ]);
    setMessageCount(0);
    setShowFeedback(false);
    setShowSuggestion(false);
    setFeedbackSent(new Set());
    setCurrentFeedback({ messageId: null, isLike: null });
    setSuggestionText("");
    setCurrentTraceId(null);
    clearSessionId();

    // Best-effort: cancella la conversazione precedente dallo store (checkpointer).
    // Non blocca il reset della UI; se fallisce si logga soltanto (il thread al
    // massimo resta orfano, esattamente come prima di questa feature).
    if (previousSessionId) {
      axiosInstance
        .delete(
          `${API_BASE_URL}${API_ENDPOINTS.SESSIONS}/${encodeURIComponent(previousSessionId)}`
        )
        .then(() =>
          console.log("Conversazione precedente cancellata dal server:", previousSessionId)
        )
        .catch((e) =>
          console.warn(
            "Cancellazione conversazione fallita (reset comunque effettuato):",
            e?.message
          )
        );
    }

    console.log("Chat resettata - Session ID e Trace ID cancellati");
  };

  const handleFeedback = (messageId, isLike) => {
    if (feedbackSent.has(messageId)) return;

    setCurrentFeedback({ messageId, isLike });
    setShowSuggestion(true);
  };

  const handleSuggestion = async () => {
    if (currentTraceId) {
      const score = currentFeedback.isLike ? 1.0 : 0.0;
      await sendScoreToBackend(currentTraceId, score, suggestionText);
    }

    setFeedbackSent((prev) => new Set([...prev, currentFeedback.messageId]));
    setShowFeedback(true);
    setShowSuggestion(false);
    setSuggestionText("");
    setCurrentFeedback({ messageId: null, isLike: null });
  };

  const handleCancelSuggestion = () => {
    setShowSuggestion(false);
    setSuggestionText("");
    setCurrentFeedback({ messageId: null, isLike: null });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="trenitalia-chat-overlay active" onClick={onClose}>
        <div
          className="trenitalia-chat-container active"
          onClick={(e) => e.stopPropagation()}
          style={{ width: `${chatWidth}px` }}
        >
          <div className="trenitalia-chat-header">
            <div className="trenitalia-chat-header-left">
              <TrenitaliaLogo size={32} className="trenitalia-chat-header" />
              <div>
                <span className="trenitalia-chat-header-title">
                  Assistente Trenitalia
                </span>
                <div className="trenitalia-chat-header-subtitle">
                  {sessionId
                    ? `Sessione: ${sessionId.slice(-8)}`
                    : "Nuova sessione"}
                </div>
              </div>
            </div>
            <div className="trenitalia-chat-controls">
              <button
                className={`trenitalia-chat-control-btn config-btn ${
                  isConfigModified() ? "modified" : ""
                }`}
                onClick={() => setShowConfig(true)}
                title={
                  isConfigModified()
                    ? "Configurazione personalizzata attiva"
                    : "Configura parametri"
                }
              >
                ⚙️
                {isConfigModified() && (
                  <span className="trenitalia-chat-config-indicator">•</span>
                )}
              </button>
              <button
                className="trenitalia-chat-control-btn"
                onClick={() => navigate("/faq")}
              >
                &#9432;
              </button>
              <button
                className="trenitalia-chat-control-btn"
                onClick={handleRefresh}
              >
                ↻
              </button>
              <button className="trenitalia-chat-control-btn" onClick={onClose}>
                ✕
              </button>
            </div>
          </div>

          <div className="trenitalia-chat-messages" ref={chatMessagesRef}>
            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                onFeedback={handleFeedback}
                feedbackSent={feedbackSent.has(message.id)}
                onTableResize={handleTableResize}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="trenitalia-chat-input-area">
            <FeedbackArea
              visible={showFeedback}
              onHide={() => setShowFeedback(false)}
            />

            {showSuggestion && (
              <div className="trenitalia-chat-suggestion-area active">
                <div className="trenitalia-chat-suggestion-message">
                  {currentFeedback.isLike
                    ? "Grazie per il feedback positivo! Vuoi aggiungere un commento?"
                    : "Ci dispiace che la risposta non sia stata utile. Aiutaci a migliorare:"}
                </div>
                <input
                  type="text"
                  className="trenitalia-chat-suggestion-input"
                  placeholder="Aggiungi un commento (facoltativo)"
                  value={suggestionText}
                  onChange={(e) => setSuggestionText(e.target.value)}
                />
                <div className="trenitalia-chat-suggestion-buttons">
                  <button
                    className="trenitalia-chat-suggestion-btn send"
                    onClick={handleSuggestion}
                  >
                    Invia
                  </button>
                  <button
                    className="trenitalia-chat-suggestion-btn cancel"
                    onClick={handleCancelSuggestion}
                  >
                    Annulla
                  </button>
                </div>
              </div>
            )}

            <QuickActions
              actions={quickActions}
              onAction={handleSendMessage}
              visible={messageCount === 0}
            />

            <ChatInput
              onSend={handleSendMessage}
              disabled={isTyping}
              placeholder="Digita il tuo messaggio"
            />
          </div>
        </div>
      </div>

      <ConfigModal
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        config={config}
        onSave={saveConfig}
      />
    </>
  );
};
